import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { List as DxList } from 'devextreme-react/list';
import type { ItemClickEvent } from 'devextreme/ui/list';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoCheckbox } from '@nuvolo/nuux-core/components/NuvoCheckbox';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import type ListProps from './ListProps';
import Styles from './Styles';
import SearchBox from './SearchBox';
import renderItem from './renderItem';
import useFullscreen from '../../hooks/useFullscreen';
import useDataStore from '#/common/hooks/useDataStore';
import DataSource from 'devextreme/data/data_source';
import useSearchQuery from '#/common/hooks/useSearchQuery';
import { useSearchParams } from 'react-router-dom';
import { TMMChip } from '#/common/utils/wrappers/withChip';
import { EventInfo } from 'devextreme/events';
import dxList from 'devextreme/ui/list';
import { splitCamelCase } from '#/common/utils/formatters/splitCamelCase';
import { toTitleCase } from '#/common/utils/formatters';
import { isUUID } from '#/common/utils/patterns/uuid';
import { CheckBox } from 'devextreme-react/check-box';
import If from '../If';
import FAIcon from '#/common/components/FAIcon';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import {
  chipsToFilters,
  DevExtremeFilterTriple,
  FilterChip,
  filtersToChips,
  flattenDevExtremeFilter,
  LogicalOperator,
  normalizeAnyOfForBackend,
} from './util';
import FilterButton, { NotificationBubble } from './FilterButton';
import FilterModal from './FilterModal';
import { FilterChips } from './FilterChips';
import { useSwitch } from '@nuvolo/nuux-core/hooks/useSwitch';
import { FilterOptions } from './ListProps';
import useSelectMode from '#/common/hooks/useSelectMode';

const FloatingButton = styled.div`
  position: fixed;
  bottom: 100px;
  right: 16px;
  background-color: ${color('contrast', 'dark')};
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const filterAlwaysFales: DevExtremeFilterTriple = ['createdAt', '=', '1970-07-25 07:29:12.517'];

const DEFAULT_FILTER_OPTIONS: FilterOptions = { title: '', groups: [] };

const List = <T,>(props: ListProps<T>) => {
  const {
    onClick,
    refresh,
    additionalFilters = [],
    getItemsQuery,
    columnList = [],
    showRelatedLists = false,
    permanentFilters,
    floatingButtonOnClick,
    setRef,
    sortingRules,
    isActionDeleteOnly,
    filterOptions = DEFAULT_FILTER_OPTIONS,
    ...itemProps
  } = props;
  const query = useSearchQuery();

  const [searchParams] = useSearchParams();
  const { selectModeOn } = useSelectMode();
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  // URL -> chips
  const urlFilters = useMemo(() => {
    try {
      return filtersToChips(query.filters, filterOptions);
    } catch (error) {
      console.error('Failed to parse filters from URL:', error);
      return [];
    }
  }, [query.filters, filterOptions]);

  const [activeFilters, setActiveFilters] = useState<FilterChip[]>(urlFilters);
  const { value: filterModalOpen, on: openFilter, off: closeFilter } = useSwitch(false);

  //header visibility & last scroll
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollTopRef = useRef(0);
  // Flag to skip the synthetic scroll event DxList emits after resizing
  const ignoreNextScrollRef = useRef(false);

  // queryFilters = whatever is in the URL (what we set with buildFiltersFromChips)
  const queryFilters = useMemo(() => query.filters, [query.filters]);

  // For generic "column" chips (ones not covered by filterGroups)
  const flatForColumns = useMemo(() => flattenDevExtremeFilter(query.filters), [query.filters]);

  const filterColumns = useMemo(
    () => Array.from(new Set(flatForColumns.map(([field]) => field))),
    [flatForColumns],
  );

  const [search, setSearch] = useState(query.search || '');

  const list = useRef<DxList>(null);

  useEffect(() => {
    if (list.current && setRef) {
      setRef(list.current!);
    }
  }, [list.current, setRef]);

  // Normalize anyof for the backend, but keep `queryFilters` (with anyof) in the URL
  const backendFilters = useMemo(() => normalizeAnyOfForBackend(queryFilters), [queryFilters]);
  const dsFilters = useMemo(() => {
    const keys = [
      ...new Set([itemProps.titleKey, itemProps.subtitleKey, ...additionalFilters].filter(Boolean)),
    ] as string[];

    const searchFilters: unknown[] = [];
    if (search) {
      keys.forEach((key) => {
        searchFilters.push([key, key === 'number' ? '=' : 'contains', search]);
        searchFilters.push('OR');
      });
      searchFilters.push(filterAlwaysFales);
    }

    const parts = [permanentFilters, backendFilters, searchFilters].filter(
      (f) => !!f && (Array.isArray(f) ? f.length > 0 : true),
    );

    if (parts.length === 0) return [];
    if (parts.length === 1) return parts[0] as unknown[];

    const combined: unknown[] = [];
    parts.forEach((part, index) => {
      combined.push(part);
      if (index < parts.length - 1) combined.push('AND');
    });
    return combined;
  }, [
    backendFilters,
    search,
    permanentFilters,
    additionalFilters,
    itemProps.titleKey,
    itemProps.subtitleKey,
  ]);

  const dataStore = useDataStore<T>({
    getItemList: getItemsQuery,
    filter: dsFilters,
  });

  const dataSource = useMemo(
    () =>
      new DataSource({
        store: dataStore,
        paginate: true,
        pageSize: 10,
        sort: sortingRules,
      }),
    [dataStore],
  );

  const onItemClick = useCallback(
    (e: ItemClickEvent<T>) => {
      const data = e.itemData as T & { id?: string };
      if (selectModeOn) {
        setSelectedItems((prev) =>
          prev.includes(data) ? prev.filter((x) => x !== data) : [...prev, data],
        );
        return;
      }

      if (onClick && data) onClick(data);
    },
    [onClick, setSelectedItems, selectModeOn],
  );

  const onSearch = useCallback(
    (newSearch: string) => {
      query.setSearchQuery({ search: newSearch, filters: query.filters });
      setSearch(newSearch);
    },
    [query],
  );

  const { height } = useViewport();
  const { isFullscreen } = useFullscreen();

  // heading & paddings & stuff
  const heightToRemove = isFullscreen ? 120 : 470;
  const hiddenHeaderHeightInPx = isHeaderVisible ? 155 : 260;
  const listHeight = height - heightToRemove + hiddenHeaderHeightInPx;

  const removeQueryParam = (paramKey: string) => {
    if (!query.filters || query.filters.length === 0) {
      return;
    }

    // Simple: drop all conditions for this field
    const flat = flattenDevExtremeFilter(query.filters).filter(([field]) => field !== paramKey);

    // Rebuild a simple AND-only expression from the remaining triples
    const rebuilt: (DevExtremeFilterTriple | LogicalOperator)[] = [];
    flat.forEach(([field, op, value], index) => {
      rebuilt.push([field, op, value]);
      if (index < flat.length - 1) rebuilt.push('AND');
    });

    query.setSearchQuery({ filters: rebuilt });
  };

  const itemRender = useCallback(
    (item: T & { id?: string }) => {
      const isSelected = selectedItems.includes(item);
      const base = renderItem({
        ...itemProps,
        isItemSelected: isSelected,
        selectModeOn,
      });
      return base(item);
    },
    [itemProps, selectedItems, selectModeOn],
  );

  const clearSelectedItems = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const correctHeight = useCallback(() => {
    const el = list.current?.instance?.element();
    if (!el) return;
    const containerHeight = el.parentElement?.clientHeight;
    if (containerHeight) {
      el.style.height = `${containerHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (!selectModeOn) {
      clearSelectedItems();
    }
    // Recalculate height — entering/exiting select mode hides/shows the search header
    correctHeight();
  }, [selectModeOn, clearSelectedItems, correctHeight]);

  // When the scroll-hide header show/hides, the layout shifts — correct the height again.
  useEffect(() => {
    correctHeight();
  }, [isHeaderVisible, correctHeight]);

  const setHeight = useCallback((e: EventInfo<dxList<T, string>>) => {
    const containerHeight = e.element?.parentElement?.clientHeight;
    if (containerHeight) {
      e.element.style.height = `${containerHeight}px`;
    }
  }, []);

  // handle scroll direction -> hide/show header
  const handleScroll = useCallback(
    (e: any) => {
      if (selectModeOn) return;

      // After toggling the header the DxList resizes and fires one synthetic
      // scroll event with a shifted position — skip it to break the feedback loop.
      if (ignoreNextScrollRef.current) {
        ignoreNextScrollRef.current = false;
        lastScrollTopRef.current = e?.scrollOffset?.top ?? 0;
        return;
      }

      const currentTop = e?.scrollOffset?.top ?? 0;
      const lastTop = lastScrollTopRef.current;
      const delta = currentTop - lastTop;

      // Ignore micro-movements
      if (Math.abs(delta) < 5) {
        lastScrollTopRef.current = currentTop;
        return;
      }

      if (delta > 0 && isHeaderVisible) {
        // Scrolling down -> hide header
        setIsHeaderVisible(false);
        ignoreNextScrollRef.current = true;
      } else if (delta < 0 && !isHeaderVisible) {
        // Scrolling up -> show header
        setIsHeaderVisible(true);
        ignoreNextScrollRef.current = true;
      }

      // Always show when at very top
      if (currentTop <= 0 && !isHeaderVisible) {
        setIsHeaderVisible(true);
        ignoreNextScrollRef.current = true;
      }

      lastScrollTopRef.current = currentTop;
    },
    [isHeaderVisible, selectModeOn],
  );

  // Hide generic column chips for fields that are already represented in filterGroups
  const groupFields = useMemo(
    () => new Set(filterOptions?.groups?.map((g) => g.dataField).filter(Boolean) as string[]),
    [filterOptions],
  );

  const filteredCols = useMemo(
    () =>
      filterColumns.filter((colName) => {
        const colValue = searchParams.get(colName || '') ?? '';
        const isCoveredByFilterGroup = groupFields.has(colName || '');
        return (!isUUID(colValue) || showRelatedLists) && !isCoveredByFilterGroup;
      }),
    [filterColumns, searchParams, showRelatedLists, groupFields],
  );

  const handleOnClick = useCallback(() => {
    if (selectedItems?.length > 0 && floatingButtonOnClick) {
      floatingButtonOnClick(selectedItems, clearSelectedItems);
    }
  }, [floatingButtonOnClick, selectedItems, clearSelectedItems]);

  const applyFilters = (newFilters: FilterChip[]) => {
    setActiveFilters(newFilters);

    const chipFilters = chipsToFilters(newFilters);
    query.setSearchQuery({
      search: query.search,
      filters: chipFilters,
    });

    closeFilter();
  };

  return (
    <Styles>
      <NuvoBox display="flex" flexDirection="column" gap={16} height="100%">
        {/* SEARCH + FILTER BUTTON */}
        <If condition={!selectModeOn && isHeaderVisible}>
          <NuvoBox display="flex" flexDirection="row" alignItems="stretch" gap={8}>
            <SearchBox onSearch={onSearch} defaultValue={query.search} />
            <If condition={!!filterOptions?.groups?.length}>
              <FilterButton activeFilters={activeFilters} openFilter={openFilter} />
            </If>
          </NuvoBox>
        </If>

        {/* SELECT MODE HEADER */}
        <If condition={selectModeOn}>
          <NuvoBox display="flex" flexDirection="row" alignItems="center" marginTop={12}>
            {selectedItems?.length === 0 ? (
              <NuvoCheckbox text="Select All" value={false} disabled />
            ) : (
              <div className="deselect-all" onClick={clearSelectedItems}>
                <CheckBox
                  text="Deselect All"
                  defaultValue={null}
                  elementAttr={{ 'aria-label': 'indeterminate' }}
                />
              </div>
            )}
            <span style={{ marginLeft: 4, fontSize: '0.9em' }}>
              {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
            </span>
          </NuvoBox>
        </If>

        {/* ACTIVE FILTER CHIPS */}
        {!selectModeOn && isHeaderVisible && activeFilters.length > 0 && (
          <FilterChips activeFilters={activeFilters} onFilterChange={applyFilters} />
        )}

        {/* COLUMN FILTER CHIPS */}
        <If condition={filteredCols?.length > 0 && isHeaderVisible}>
          <div>
            {filteredCols.map((colName) => (
              <TMMChip
                label={colName
                  .split('.')
                  .map((partialIdentifier) => splitCamelCase(toTitleCase(partialIdentifier)))
                  .join('.')}
                key={colName}
                canDelete
                type="info"
                onDelete={() => removeQueryParam(colName)}
              />
            ))}
          </div>
        </If>

        {/* LIST */}
        <NuvoBox flex="1">
          <DxList
            ref={list}
            className="list-wrapper"
            height={listHeight}
            dataSource={dataSource}
            onItemClick={onItemClick}
            pullRefreshEnabled={!!refresh}
            itemRender={itemRender}
            onPullRefresh={refresh}
            onContentReady={setHeight}
            onScroll={handleScroll}
          />
        </NuvoBox>
      </NuvoBox>

      {filterModalOpen && (
        <FilterModal
          // isOpen={filterModalOpen}
          onCancel={closeFilter}
          title={filterOptions?.title}
          onApply={applyFilters}
          groups={filterOptions?.groups}
          activeFilters={activeFilters}
        />
      )}

      <If condition={selectModeOn}>
        <FloatingButton onClick={handleOnClick}>
          <If condition={!!selectedItems?.length}>
            <NotificationBubble>
              {selectedItems?.length > 99 ? '99+' : selectedItems?.length || 0}
            </NotificationBubble>
          </If>

          <If condition={isActionDeleteOnly}>
            <FAIcon icon="trash" color={color('contrast', 'light')} />
          </If>
          <If condition={!isActionDeleteOnly}>
            <FAIcon icon="money-check-pen" color={color('contrast', 'light')} />
          </If>
        </FloatingButton>
      </If>
    </Styles>
  );
};

export default React.memo(List) as typeof List;
